import { useOrgStore } from '@/stores/org-store';
import { apiRequest } from '../../utils/api-request';
import { StructuredUpload } from '@/types/source-types';
import { shouldUseMultipartUpload, multipartUpload } from './multipart-upload';
import * as Sentry from '@sentry/react';

export interface UploadFailedFile {
  original_filename: string;
  error: string;
  canceled?: boolean;
}

export interface UploadSourceResponse {
  data?: {
    status: 'success' | 'error' | 'partial_success';
    message: string;
    uploaded_files: {
      original_filename: string;
      s3_url: string;
      file_info: {
        filename: string;
        file_size_mb: string;
        mime_type: string;
      };
      celery_task_id?: string;
      ingestion_method?: string;
      ingestion_status?: string;
    }[];
    failed_files?: UploadFailedFile[];
    structured_uploads?: StructuredUpload[];
  };
  detail?: string;
}

export type UploadProgressPhase =
  | 'queued'
  | 'uploading'
  | 'completing'
  | 'done'
  | 'failed'
  | 'canceled';

export interface UploadProgressEvent {
  fileId: string;
  filename: string;
  size: number;
  phase: UploadProgressPhase;
  percent: number; // 0..100
  statusMessage?: string;
  error?: string;
}

// Keep existing UploadSourceResponse / UploadProgressEvent / UploadProgressPhase
// unchanged above this line.

const MAX_PARALLEL_MULTIPART_UPLOADS = 3;

interface UploadSourceProps {
  files: File[];
  id?: string;
  collection?: string;
  collectionDataType?: 'structured' | 'unstructured' | null;
  fieldOverrides?: Record<string, unknown>;
  multipartMimeTypes?: string[];
  appEncryptionEnabled?: boolean;
  signal?: AbortSignal;
  onProgress?: (event: UploadProgressEvent) => void;
}

export const fileIdOf = (file: File): string =>
  `${file.name}-${file.size}-${file.lastModified}`;

interface MultipartPoolResult {
  uploaded: NonNullable<UploadSourceResponse['data']>['uploaded_files'];
  failed: UploadFailedFile[];
}

async function runMultipartWithBoundedConcurrency(
  files: File[],
  orgId: string,
  collection: string,
  fieldOverrides: Record<string, unknown> | undefined,
  signal: AbortSignal | undefined,
  onProgress: ((e: UploadProgressEvent) => void) | undefined,
): Promise<MultipartPoolResult> {
  const uploaded: NonNullable<UploadSourceResponse['data']>['uploaded_files'] =
    [];
  const failed: UploadFailedFile[] = [];
  const queue = [...files];
  const inFlight: Promise<void>[] = [];

  const runOne = async (file: File): Promise<void> => {
    const fileId = fileIdOf(file);
    try {
      if (signal?.aborted) {
        onProgress?.({
          fileId,
          filename: file.name,
          size: file.size,
          phase: 'canceled',
          percent: 0,
        });
        failed.push({
          original_filename: file.name,
          error: 'canceled',
          canceled: true,
        });
        return;
      }
      onProgress?.({
        fileId,
        filename: file.name,
        size: file.size,
        phase: 'uploading',
        percent: 0,
      });
      const result = await multipartUpload(
        file,
        orgId,
        collection,
        (percent) => {
          const phase: UploadProgressPhase =
            percent >= 95 ? 'completing' : 'uploading';
          onProgress?.({
            fileId,
            filename: file.name,
            size: file.size,
            phase,
            percent,
          });
        },
        signal,
        fieldOverrides ? { fieldOverrides } : {},
      );
      onProgress?.({
        fileId,
        filename: file.name,
        size: file.size,
        phase: 'done',
        percent: 100,
      });
      uploaded.push({
        original_filename: file.name,
        s3_url: `s3://${result.s3_bucket}/${result.s3_key}`,
        file_info: {
          filename: result.s3_key.split('/').pop() || file.name,
          file_size_mb: (result.total_size / (1024 * 1024)).toFixed(2),
          mime_type: result.content_type,
        },
        celery_task_id: result.celery_task_id,
        ingestion_method: result.ingestion_method,
        ingestion_status: result.ingestion_status,
      });
    } catch (err) {
      const isAbort = err instanceof DOMException && err.name === 'AbortError';
      const errorMessage = err instanceof Error ? err.message : String(err);
      onProgress?.({
        fileId,
        filename: file.name,
        size: file.size,
        phase: isAbort ? 'canceled' : 'failed',
        percent: 0,
        error: errorMessage,
      });
      failed.push({
        original_filename: file.name,
        error: errorMessage,
        canceled: isAbort || undefined,
      });
      if (!isAbort) {
        Sentry.captureException(err, {
          extra: {
            context: 'upload-sources:pool',
            filename: file.name,
            fileSize: file.size,
            collectionId: collection,
          },
        });
      }
    }
  };

  while (queue.length > 0 || inFlight.length > 0) {
    while (
      inFlight.length < MAX_PARALLEL_MULTIPART_UPLOADS &&
      queue.length > 0
    ) {
      const file = queue.shift()!;
      const p = runOne(file).finally(() => {
        const idx = inFlight.indexOf(p);
        if (idx >= 0) inFlight.splice(idx, 1);
      });
      inFlight.push(p);
    }
    if (inFlight.length > 0) {
      await Promise.race(inFlight);
    }
  }

  return { uploaded, failed };
}

async function uploadAllSinglePart(
  files: File[],
  orgId: string,
  collection: string,
  fieldOverrides: Record<string, unknown> | undefined,
  signal: AbortSignal | undefined,
  onProgress: ((e: UploadProgressEvent) => void) | undefined,
): Promise<UploadSourceResponse> {
  if (signal?.aborted) {
    files.forEach((f) =>
      onProgress?.({
        fileId: fileIdOf(f),
        filename: f.name,
        size: f.size,
        phase: 'canceled',
        percent: 0,
      }),
    );
    return { detail: 'Upload canceled' };
  }

  files.forEach((f) =>
    onProgress?.({
      fileId: fileIdOf(f),
      filename: f.name,
      size: f.size,
      phase: 'uploading',
      percent: 10,
    }),
  );

  const formData = new FormData();
  files.forEach((file) => formData.append('files', file));
  if (fieldOverrides && Object.keys(fieldOverrides).length > 0) {
    formData.append('field_overrides', JSON.stringify(fieldOverrides));
  }

  const response = await apiRequest<UploadSourceResponse>({
    url: `/uploads/${orgId}/${collection}`,
    method: 'POST',
    target: 'platform',
    formData: true,
    body: formData,
    signal,
  });

  const isAbort = signal?.aborted === true;

  if (response?.detail) {
    files.forEach((f) =>
      onProgress?.({
        fileId: fileIdOf(f),
        filename: f.name,
        size: f.size,
        phase: isAbort ? 'canceled' : 'failed',
        percent: 0,
        error: isAbort ? undefined : response.detail,
      }),
    );
    return { detail: response.detail || 'Failed to upload data source' };
  }

  // Honor the server's per-file outcome: a single-part POST can return
  // partial_success with some files in failed_files. Mark only the files the
  // server confirmed uploaded as done; everything in failed_files as failed.
  const data = (response as UploadSourceResponse).data;
  const failedByName = new Map<string, UploadFailedFile>(
    (data?.failed_files ?? []).map((f) => [f.original_filename, f]),
  );

  files.forEach((f) => {
    const failure = failedByName.get(f.name);
    if (failure) {
      onProgress?.({
        fileId: fileIdOf(f),
        filename: f.name,
        size: f.size,
        phase: failure.canceled ? 'canceled' : 'failed',
        percent: 0,
        error: failure.canceled ? undefined : failure.error,
      });
    } else {
      onProgress?.({
        fileId: fileIdOf(f),
        filename: f.name,
        size: f.size,
        phase: 'done',
        percent: 100,
      });
    }
  });

  return response as UploadSourceResponse;
}

export const uploadSources = async ({
  id = '',
  collection,
  collectionDataType,
  files = [],
  fieldOverrides,
  multipartMimeTypes,
  appEncryptionEnabled,
  signal,
  onProgress,
}: UploadSourceProps): Promise<UploadSourceResponse> => {
  const orgId = id || useOrgStore.getState().currentOrg?.orgid;
  if (!orgId) throw new Error('Organization ID is required');
  if (!collection) throw new Error('Collection name is required');

  files.forEach((f) =>
    onProgress?.({
      fileId: fileIdOf(f),
      filename: f.name,
      size: f.size,
      phase: 'queued',
      percent: 0,
    }),
  );

  // App-level encryption: the presigned multipart path is blocked server-side
  // (bytes would bypass envelope encryption). Send everything through the
  // encryption-aware single-part POST, capped at the policy's max_upload_size.
  if (appEncryptionEnabled) {
    return uploadAllSinglePart(
      files,
      orgId,
      collection,
      fieldOverrides,
      signal,
      onProgress,
    );
  }

  // Unstructured: every file through multipart with bounded concurrency.
  if (collectionDataType === 'unstructured') {
    const { uploaded, failed } = await runMultipartWithBoundedConcurrency(
      files,
      orgId,
      collection,
      fieldOverrides,
      signal,
      onProgress,
    );
    const hasUploaded = uploaded.length > 0;
    const hasFailed = failed.length > 0;
    const status: 'success' | 'error' | 'partial_success' = hasFailed
      ? hasUploaded
        ? 'partial_success'
        : 'error'
      : 'success';
    const message = hasFailed
      ? hasUploaded
        ? `${uploaded.length} uploaded, ${failed.length} failed`
        : `All ${failed.length} file(s) failed to upload`
      : `${uploaded.length} file(s) uploaded via multipart`;
    return {
      data: {
        status,
        message,
        uploaded_files: uploaded,
        failed_files: hasFailed ? failed : undefined,
      },
      ...(status === 'error' ? { detail: message } : {}),
    };
  }

  // Structured: keep existing size-threshold split + POST fallback.
  const largeFiles = files.filter((f) =>
    shouldUseMultipartUpload(f, {
      dataType: collectionDataType,
      multipartMimeTypes,
    }),
  );
  const regularFiles = files.filter(
    (f) =>
      !shouldUseMultipartUpload(f, {
        dataType: collectionDataType,
        multipartMimeTypes,
      }),
  );

  const { uploaded: multipartUploaded, failed: multipartFailed } =
    await runMultipartWithBoundedConcurrency(
      largeFiles,
      orgId,
      collection,
      fieldOverrides,
      signal,
      onProgress,
    );

  if (regularFiles.length > 0) {
    regularFiles.forEach((f) =>
      onProgress?.({
        fileId: fileIdOf(f),
        filename: f.name,
        size: f.size,
        phase: 'uploading',
        percent: 10,
      }),
    );

    const formData = new FormData();
    regularFiles.forEach((file) => formData.append('files', file));
    if (fieldOverrides && Object.keys(fieldOverrides).length > 0) {
      formData.append('field_overrides', JSON.stringify(fieldOverrides));
    }

    const response = await apiRequest<UploadSourceResponse>({
      url: `/uploads/${orgId}/${collection}`,
      method: 'POST',
      target: 'platform',
      formData: true,
      body: formData,
    });

    if (response?.detail) {
      regularFiles.forEach((f) =>
        onProgress?.({
          fileId: fileIdOf(f),
          filename: f.name,
          size: f.size,
          phase: 'failed',
          percent: 0,
          error: response.detail,
        }),
      );
      return { detail: response.detail || 'Failed to upload data source' };
    }

    regularFiles.forEach((f) =>
      onProgress?.({
        fileId: fileIdOf(f),
        filename: f.name,
        size: f.size,
        phase: 'done',
        percent: 100,
      }),
    );

    const responseData = (response as UploadSourceResponse).data;
    if (responseData) {
      if (multipartUploaded.length > 0) {
        responseData.uploaded_files = [
          ...multipartUploaded,
          ...responseData.uploaded_files,
        ];
      }
      if (multipartFailed.length > 0) {
        responseData.failed_files = [
          ...multipartFailed,
          ...(responseData.failed_files ?? []),
        ];
        if (responseData.status === 'success') {
          responseData.status =
            responseData.uploaded_files.length > 0
              ? 'partial_success'
              : 'error';
        }
      }
    }
    return response as UploadSourceResponse;
  }

  const hasUploaded = multipartUploaded.length > 0;
  const hasFailed = multipartFailed.length > 0;
  const status: 'success' | 'error' | 'partial_success' = hasFailed
    ? hasUploaded
      ? 'partial_success'
      : 'error'
    : 'success';
  const message = hasFailed
    ? hasUploaded
      ? `${multipartUploaded.length} uploaded, ${multipartFailed.length} failed`
      : `All ${multipartFailed.length} file(s) failed to upload`
    : `${multipartUploaded.length} file(s) uploaded via multipart`;
  return {
    data: {
      status,
      message,
      uploaded_files: multipartUploaded,
      failed_files: hasFailed ? multipartFailed : undefined,
    },
    ...(status === 'error' ? { detail: message } : {}),
  };
};
